import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, TrendingUp, Zap, Target, Award, ArrowLeft } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { database } from './firebase';
import { ref, query, orderByChild, get } from 'firebase/database';

export default function TournoiComparison() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') !== 'false');
  
  // États
  const [matchesData, setMatchesData] = useState([]);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Charger automatiquement les 20 derniers matchs au montage
  useEffect(() => {
    loadRecentMatches();
  }, []);

  // Charger TOUS les matchs depuis Firebase
  const loadRecentMatches = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const matchesRef = ref(database, 'matches');
      const q = query(matchesRef, orderByChild('createdAt'));
      
      const snapshot = await get(q);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        
        // Convertir en tableau et trier du plus récent au plus ancien
        const matchesArray = Object.entries(data)
          .map(([id, match]) => ({
            id,
            ...match
          }))
          .sort((a, b) => b.createdAt - a.createdAt); // Du plus récent au plus ancien
        
        setMatchesData(matchesArray);
        
        // Pré-sélectionner les 5 derniers matchs
        const recentIds = matchesArray.slice(0, 5).map(m => m.id);
        setSelectedMatches(recentIds);
      } else {
        setMatchesData([]);
        setError("Aucun match trouvé.");
      }
    } catch (err) {
      console.error('Error loading matches:', err);
      setError("Erreur lors du chargement des matchs.");
    } finally {
      setLoading(false);
    }
  };

  // Toggle sélection match
  const toggleMatch = (matchId) => {
    setSelectedMatches(prev => 
      prev.includes(matchId)
        ? prev.filter(id => id !== matchId)
        : [...prev, matchId]
    );
  };

  // Calculer les classements joueurs
  const calculatePlayerRankings = () => {
    const playerStats = {};
    
    // Agréger les stats de tous les matchs sélectionnés
    selectedMatches.forEach(matchId => {
      const match = matchesData.find(m => m.id === matchId);
      if (!match) return;
      
      const { config, playerStats: stats } = match;
      
      config.players.forEach(player => {
        // Normaliser le nom en minuscules pour éviter les doublons (Pierre vs pierre)
        const normalizedName = player.name.toLowerCase().trim();
        
        if (!playerStats[normalizedName]) {
          playerStats[normalizedName] = {
            name: player.name, // Garde le premier nom trouvé (avec sa casse originale)
            winners: 0,
            faults: 0,
            total: 0,
            ratio: 0,
            colorHex: player.colorHex
          };
        }
        
        const w = stats[player.id]?.winners || 0;
        const f = stats[player.id]?.faults || 0;
        
        playerStats[normalizedName].winners += w;
        playerStats[normalizedName].faults += f;
        playerStats[normalizedName].total += (w + f);
      });
    });
    
    // Calculer les ratios et trier
    const rankings = Object.values(playerStats)
      .map(p => ({
        ...p,
        ratio: p.total > 0 ? ((p.winners / p.total) * 100).toFixed(1) : 0
      }))
      .sort((a, b) => parseFloat(b.ratio) - parseFloat(a.ratio));
    
    return rankings;
  };

  // Calculer les classements équipes
  const calculateTeamRankings = () => {
    const teamStats = {};
    
    // Agréger les stats de tous les matchs sélectionnés
    selectedMatches.forEach(matchId => {
      const match = matchesData.find(m => m.id === matchId);
      if (!match) return;
      
      const { config, playerStats: stats } = match;
      
      // Identifier les équipes (team 1 et team 2)
      const team1Players = config.players.filter(p => p.team === 1);
      const team2Players = config.players.filter(p => p.team === 2);
      
      // Créer les clés d'équipe normalisées (tri alphabétique)
      const createTeamKey = (players) => {
        return players
          .map(p => p.name.trim())
          .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
          .join(' & ');
      };
      
      const team1Key = createTeamKey(team1Players);
      const team2Key = createTeamKey(team2Players);
      
      // Agréger stats team 1
      if (!teamStats[team1Key]) {
        teamStats[team1Key] = {
          teamName: team1Key,
          winners: 0,
          faults: 0,
          total: 0,
          ratio: 0
        };
      }
      
      team1Players.forEach(player => {
        const w = stats[player.id]?.winners || 0;
        const f = stats[player.id]?.faults || 0;
        teamStats[team1Key].winners += w;
        teamStats[team1Key].faults += f;
        teamStats[team1Key].total += (w + f);
      });
      
      // Agréger stats team 2
      if (!teamStats[team2Key]) {
        teamStats[team2Key] = {
          teamName: team2Key,
          winners: 0,
          faults: 0,
          total: 0,
          ratio: 0
        };
      }
      
      team2Players.forEach(player => {
        const w = stats[player.id]?.winners || 0;
        const f = stats[player.id]?.faults || 0;
        teamStats[team2Key].winners += w;
        teamStats[team2Key].faults += f;
        teamStats[team2Key].total += (w + f);
      });
    });
    
    // Calculer les ratios et trier
    const rankings = Object.values(teamStats)
      .map(t => ({
        ...t,
        ratio: t.total > 0 ? ((t.winners / t.total) * 100).toFixed(1) : 0
      }))
      .sort((a, b) => parseFloat(b.ratio) - parseFloat(a.ratio));
    
    return rankings;
  };

  // Obtenir les top performers
  const getTopPerformers = (rankings) => {
    if (rankings.length === 0) return null;
    
    const bestRatio = rankings[0];
    const mostWinners = [...rankings].sort((a, b) => b.winners - a.winners)[0];
    const leastFaults = [...rankings].sort((a, b) => a.faults - b.faults)[0];
    const mostRegular = [...rankings].sort((a, b) => {
      const diffA = Math.abs(a.winners - a.faults);
      const diffB = Math.abs(b.winners - b.faults);
      return diffA - diffB;
    })[0];
    
    return { bestRatio, mostWinners, leastFaults, mostRegular };
  };

  // Préparer les données pour les graphiques momentum
  const prepareMomentumData = () => {
    return selectedMatches.map(matchId => {
      const match = matchesData.find(m => m.id === matchId);
      if (!match || !match.pointHistory) return null;
      
      const { config, pointHistory } = match;
      const date = new Date(match.createdAt).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit' 
      });
      
      return {
        matchId,
        title: `Match ${date}`,
        players: config.players,
        data: pointHistory
      };
    }).filter(Boolean);
  };

  const rankings = calculatePlayerRankings();
  const teamRankings = calculateTeamRankings();
  const topPerformers = getTopPerformers(rankings);
  const momentumData = prepareMomentumData();

  // Composant graphique momentum individuel
  const MomentumChart = ({ match }) => (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 shadow-md`}>
      <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        {match.title}
      </h3>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={match.data}>
          <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
          <XAxis 
            dataKey="point" 
            stroke={darkMode ? '#9ca3af' : '#6b7280'}
            tick={{ fontSize: 10 }}
          />
          <YAxis 
            stroke={darkMode ? '#9ca3af' : '#6b7280'}
            tick={{ fontSize: 10 }}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: darkMode ? '#1f2937' : '#fff',
              border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
            }}
          />
          {match.players.map(player => (
            <Line
              key={player.id}
              type="monotone"
              dataKey={player.name}
              stroke={player.colorHex}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Chargement des matchs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} pb-8`}>
      {/* Header */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <ArrowLeft className={darkMode ? 'text-gray-300' : 'text-gray-700'} size={24} />
              </button>
              <Trophy className="text-yellow-500" size={28} />
              <h1 className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Analyse
              </h1>
            </div>
            <button
              onClick={() => {
                setDarkMode(!darkMode);
                localStorage.setItem('darkMode', (!darkMode).toString());
              }}
              className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              {darkMode ? '🌞' : '🌙'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        
        {/* Liste des matchs avec checkboxes */}
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 shadow-md`}>
          <h2 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            📋 Sélectionner les matchs ({selectedMatches.length} sélectionnés)
          </h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {matchesData.map((match, idx) => {
              const date = new Date(match.createdAt).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
              
              // Formater : "Joueur1 - Joueur2 vs Joueur3 - Joueur4"
              const team1 = match.config.players.filter(p => p.team === 1).map(p => p.name).join(' - ');
              const team2 = match.config.players.filter(p => p.team === 2).map(p => p.name).join(' - ');
              const players = `${team1} vs ${team2}`;
              
              return (
                <label
                  key={match.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedMatches.includes(match.id)
                      ? darkMode ? 'bg-blue-900/30' : 'bg-blue-50'
                      : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedMatches.includes(match.id)}
                    onChange={() => toggleMatch(match.id)}
                    className="w-5 h-5 text-blue-500 rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      {players}
                    </div>
                    <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {date}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Afficher uniquement si au moins 1 match sélectionné */}
        {selectedMatches.length > 0 && (
          <>
            {/* Classement joueurs */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 shadow-md`}>
              <h2 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                🏅 Classement des joueurs
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                      <th className={`text-left py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Rang</th>
                      <th className={`text-left py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Joueur</th>
                      <th className={`text-center py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>W</th>
                      <th className={`text-center py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>F</th>
                      <th className={`text-center py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Total</th>
                      <th className={`text-center py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Ratio %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankings.map((player, idx) => (
                      <tr key={player.name} className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'}>
                        <td className={`py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`}
                        </td>
                        <td className="py-2 px-2 flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: player.colorHex }}
                          />
                          <span className={darkMode ? 'text-gray-200' : 'text-gray-900'}>{player.name}</span>
                        </td>
                        <td className={`text-center py-2 px-2 font-semibold text-green-500`}>{player.winners}</td>
                        <td className={`text-center py-2 px-2 font-semibold text-red-500`}>{player.faults}</td>
                        <td className={`text-center py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{player.total}</td>
                        <td className={`text-center py-2 px-2 font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          {player.ratio}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Classement équipes */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 shadow-md`}>
              <h2 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                🏆 Classement des équipes
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                      <th className={`text-left py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Rang</th>
                      <th className={`text-left py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Équipe</th>
                      <th className={`text-center py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>W</th>
                      <th className={`text-center py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>F</th>
                      <th className={`text-center py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Total</th>
                      <th className={`text-center py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Ratio %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamRankings.map((team, idx) => (
                      <tr key={team.teamName} className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'}>
                        <td className={`py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`}
                        </td>
                        <td className={`py-2 px-2 font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {team.teamName}
                        </td>
                        <td className={`text-center py-2 px-2 font-semibold text-green-500`}>{team.winners}</td>
                        <td className={`text-center py-2 px-2 font-semibold text-red-500`}>{team.faults}</td>
                        <td className={`text-center py-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{team.total}</td>
                        <td className={`text-center py-2 px-2 font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          {team.ratio}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top Performers */}
            {topPerformers && teamRankings.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Meilleur ratio */}
                <div className={`${darkMode ? 'bg-gradient-to-br from-yellow-900/30 to-yellow-800/20' : 'bg-gradient-to-br from-yellow-50 to-yellow-100'} rounded-lg p-4 shadow-md`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="text-yellow-500" size={20} />
                    <h3 className={`text-sm font-semibold ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                      Meilleur ratio
                    </h3>
                  </div>
                  <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {topPerformers.bestRatio.name}
                  </p>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                    {topPerformers.bestRatio.ratio}%
                  </p>
                </div>

                {/* Plus de winners */}
                <div className={`${darkMode ? 'bg-gradient-to-br from-green-900/30 to-green-800/20' : 'bg-gradient-to-br from-green-50 to-green-100'} rounded-lg p-4 shadow-md`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="text-green-500" size={20} />
                    <h3 className={`text-sm font-semibold ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                      Plus de winners
                    </h3>
                  </div>
                  <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {topPerformers.mostWinners.name}
                  </p>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    {topPerformers.mostWinners.winners}
                  </p>
                </div>

                {/* Moins de fautes */}
                <div className={`${darkMode ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20' : 'bg-gradient-to-br from-blue-50 to-blue-100'} rounded-lg p-4 shadow-md`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="text-blue-500" size={20} />
                    <h3 className={`text-sm font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                      Moins de fautes
                    </h3>
                  </div>
                  <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {topPerformers.leastFaults.name}
                  </p>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {topPerformers.leastFaults.faults}
                  </p>
                </div>

                {/* Plus régulier */}
                <div className={`${darkMode ? 'bg-gradient-to-br from-purple-900/30 to-purple-800/20' : 'bg-gradient-to-br from-purple-50 to-purple-100'} rounded-lg p-4 shadow-md`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="text-purple-500" size={20} />
                    <h3 className={`text-sm font-semibold ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                      Plus régulier
                    </h3>
                  </div>
                  <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {topPerformers.mostRegular.name}
                  </p>
                  <p className={`text-sm ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    W-F: {topPerformers.mostRegular.winners - topPerformers.mostRegular.faults}
                  </p>
                </div>

                {/* Meilleure équipe */}
                <div className={`${darkMode ? 'bg-gradient-to-br from-orange-900/30 to-orange-800/20' : 'bg-gradient-to-br from-orange-50 to-orange-100'} rounded-lg p-4 shadow-md`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="text-orange-500" size={20} />
                    <h3 className={`text-sm font-semibold ${darkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                      Meilleure équipe
                    </h3>
                  </div>
                  <p className={`text-base font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {teamRankings[0].teamName}
                  </p>
                  <p className={`text-2xl font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                    {teamRankings[0].ratio}%
                  </p>
                </div>
              </div>
            )}

            {/* Graphiques Momentum */}
            {momentumData.length > 0 && (
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 shadow-md`}>
                <h2 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <TrendingUp className="text-blue-500" size={20} />
                  Momentum des matchs
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {momentumData.map(match => (
                    <MomentumChart key={match.matchId} match={match} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
